// routes/ResourceUploader.jsx — **파일 하나를 이 Space 에 보관시키는 자리.**
//
// 계약 정본: `docs/shared-file-api-v0.1.md`.
//
// 이 파일은 **화면**이다. 세 곳으로 나뉜 일 가운데 마지막 조각이다:
//
//   `api/upload.js`      어떤 상태에서 무엇이 다음인가 (순수 함수)
//   `api/upload-run.js`  그 걸음들을 실제로 밟는다 (네트워크는 주입받는다)
//   여기                  고르고·보이고·누르는 자리. 울타리와 네트워크를 묶어 넘긴다
//
// ## 이 조각이 지키는 것
//
//   · **`<form>` 이 없다.** 넘김·반환 대화상자 안에 그대로 들어가야 하는데, 폼 안에 폼을
//     넣으면 브라우저가 안쪽을 버린다. 제출은 버튼 하나이고 `type="button"` 이다.
//   · **바이트를 두 번 만들지 않는다.** base64 도, `FileReader` 도, 조각 이어 붙이기도 없다.
//     `File` 을 그대로 PUT 몸통에 싣는다. 해시만 한 번 메모리에 올라가고(WebCrypto 에
//     스트리밍 digest 가 없다), 그래서 능력 표의 상한을 넘는 파일은 고르는 자리에서 먼저 거절된다.
//   · **다시 하는 것은 실패한 걸음뿐이다.** 확정이 끝난 뒤 붙이기가 실패하면 **붙이기만**
//     다시 한다 — 다시 올리면 같은 파일의 판이 하나 더 생기고, 그건 재시도가 아니다.
//   · **닫아도 잃지 않는다.** 고른 파일·제목·예약은 이 컴포넌트 밖(호출자의 초안)에 산다.
//     닫으면 전송만 끊고 상태를 `interrupted` 로 되돌린다 — 예약은 서버에 남으므로
//     다시 열면 「보내기 다시 시도」로 이어진다.
//   · **떠난 일은 남의 자리에 쓰지 않는다.** 진행마다 울타리(`aliveFor`)가 붙는다.
//     unmount·범위 변경·새 시도 셋이 그 울타리를 올리고, 그 뒤에 도착한 응답은 버려진다.
//   · **없는 기능을 그리지 않는다.** 능력 표(`resource-upload-limits`)가 꺼져 있다고 하면
//     올리는 자리를 아예 만들지 않는다. 그때도 URL·git 연결은 그대로 쓸 수 있다.

import { useCallback, useEffect, useRef, useState } from 'preact/hooks';
import {
  abortResourceUpload, digestFileSha256, finalizeResourceUpload, initResourceUpload,
  newActionKey, read, transferResourceUpload,
} from '../api/client.js';
import { invalidate, useResource } from '../api/resource.js';
import { describeError } from '../api/errors.js';
import { useSession } from '../session/session.jsx';
import {
  acceptAttribute, advanceUpload, allowedExtensionsLabel, canRetryUpload, deriveTitle,
  emptyUploadState, needsFreshReservation, progressPercent, resumeStep, retryLabel,
  uploadErrorMessage, uploadPhaseLabel, uploadStepPath, UPLOAD_PHASE, UPLOAD_STEP,
  validateFileForUpload,
} from '../api/upload.js';
import {
  intentDiffers, releaseReservation, runUpload, RUN_OUTCOME,
} from '../api/upload-run.js';
import { Button, Field, Icon, Input, Notice } from '../ui/primitives.jsx';
import { fileSizeLabel } from './resource-directory.js';
import './resource-upload.css';

/**
 * 이 배포가 파일을 받나.
 *
 * 세 가지 「못 받음」을 **구분해서** 말한다. 하나로 뭉치면 운영자는 무엇을 고쳐야 할지 모른다.
 *   `disabled`   객체 루트가 설정되지 않았다(그 배포의 선택).
 *   `absent`     이 Gateway 에 아직 그 경로가 없다(배포 버전이 뒤처졌다).
 *   `error`      읽지 못했다(권한·네트워크).
 */
export function useUploadLimits(spaceId) {
  const session = useSession();
  const resource = useResource(`space:${spaceId}:upload-limits`, () =>
    read('resource-upload-limits', { nodeId: session.nodeId, query: { spaceId } }),
  );
  if (resource.status === 'loading') return { state: 'loading', limits: null, error: null };
  if (resource.status === 'error') {
    const code = resource.error?.code ?? null;
    if (code === 'managed_files_disabled' || code === 'resource_cloud_unavailable') {
      return { state: 'disabled', limits: null, error: resource.error, code };
    }
    if (code === 'route_not_found' || resource.error?.status === 404) {
      return { state: 'absent', limits: null, error: resource.error, code };
    }
    return { state: 'error', limits: null, error: resource.error, code };
  }
  return resource.data?.enabled === true
    ? { state: 'ready', limits: resource.data, error: null }
    : { state: 'disabled', limits: resource.data ?? null, error: null, code: 'managed_files_disabled' };
}

/** 능력 표가 없을 때 **그 이유만** 한 줄로. 여기서 업로드를 흉내 내지 않는다. */
export function UploadUnavailableNotice({ capability }) {
  if (capability.state === 'loading') return <p class="t-meta">파일 보관 설정을 확인하는 중입니다.</p>;
  if (capability.state === 'ready') return null;
  const text = {
    disabled: '이 서버에서는 파일 보관이 꺼져 있습니다. URL·git 링크는 그대로 쓸 수 있습니다.',
    absent: '이 서버에는 파일 보관 기능이 아직 없습니다. URL·git 링크는 그대로 쓸 수 있습니다.',
    error: '파일 보관 설정을 읽지 못했습니다. URL·git 링크는 그대로 쓸 수 있습니다.',
  }[capability.state] ?? '파일 보관 상태를 알 수 없습니다.';
  return <p class="t-meta">{text}</p>;
}

/**
 * @param draft    `{ title, file, upload }` — **호출자가 들고 있다.** 대화상자가 닫혀도 남는다.
 *                 `file` 은 브라우저의 `File` 핸들이다(바이트 사본이 아니고, 저장소에 쓰지 않는다).
 * @param attach   있으면 확정 뒤 이것을 부른다. `false` 를 돌려주면 **붙이기만** 다시 할 수 있다.
 * @param onUploaded 확정된 manifest. attach 가 없으면 이것으로 끝난다.
 */
export function ResourceUploader({
  spaceId,
  assetId = null,
  limits,
  draft,
  setDraft,
  attach = null,
  onUploaded = null,
  idPrefix = 'upload',
  busy = false,
}) {
  const session = useSession();
  const transferRef = useRef(null);
  const mountedRef = useRef(true);
  // 시도마다 하나씩 올라간다. 지난 시도의 응답은 이 숫자가 달라진 것으로 구분해 버린다.
  const epochRef = useRef(0);
  // **범위**가 바뀌면 그건 다른 자리다. Space·말하는 Node·새 판을 올릴 대상 자산 셋이다.
  const scopeKey = `${spaceId}\u0000${session.nodeId}\u0000${assetId ?? ''}`;
  const scopeRef = useRef(scopeKey);
  scopeRef.current = scopeKey;
  // **같은 행위가 두 번 나가지 않게.** 버튼의 `disabled` 는 다시 그린 뒤에야 붙으므로,
  // 한 프레임 안의 두 번째 클릭은 그걸 지나친다 — 그러면 예약이 둘 생기고 할당량을
  // 두 번 먹는다. 상태가 아니라 ref 라야 **지금 이 순간** 막힌다.
  const runningRef = useRef(false);
  const [dragging, setDragging] = useState(false);
  const [localError, setLocalError] = useState(null);

  const upload = draft.upload ?? emptyUploadState();
  const file = draft.file ?? null;
  const setUpload = useCallback((event) => {
    setDraft((prior) => ({ ...prior, upload: advanceUpload(prior.upload ?? emptyUploadState(), event) }));
  }, [setDraft]);
  // **이 효과는 mount 당 한 번이어야 한다.** 그래서 최신 갱신 함수는 의존성이 아니라 ref 로 본다.
  // 의존성에 두면 호출자가 `setDraft` 를 인라인 함수로 넘기는 순간(렌더마다 새 정체성)
  // 정리 함수가 매 렌더 돌고, 그 정리가 다시 상태를 바꿔 렌더를 부른다 — 끝나지 않는 고리다.
  const setUploadRef = useRef(setUpload);
  setUploadRef.current = setUpload;

  // 화면이 사라지면 **전송만** 끊는다. 예약은 서버에 그대로 두고(TTL 이 정리한다),
  // 다시 열었을 때 「보내기 다시 시도」로 이어갈 수 있게 남긴다.
  //
  // 순서가 규칙이다. ① 울타리를 올리고(그 뒤에 돌아오는 응답은 아무 데도 쓰이지 않는다)
  // ② 전송을 끊고 ③ 상태를 `interrupted` 로 되돌린다. ③ 을 빠뜨리면 끊긴 전송의 약속은
  // 영원히 깨어나지 않으므로 phase 가 `transferring` 에 굳고, 다시 열었을 때 버튼이
  // 계속 비활성으로 남는다.
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      // 울타리를 먼저 무너뜨린다. 이 뒤에 돌아오는 응답은 아무것도 쓰지 않는다.
      epochRef.current += 1;
      transferRef.current?.abort();
      transferRef.current = null;
      setUploadRef.current({ type: 'interrupted' });
    };
  }, []);

  // 범위가 바뀌면 진행 중이던 것은 **다른 자리의 일**이 된다. 그 전송을 끊는다.
  // (초안 자체를 비우는 것은 호출자의 몫이다 — 여기서 지우면 남의 초안까지 지운다.)
  //
  // **cleanup 에서 끊는다.** 효과 본문은 mount 때도 돌기 때문이다. 거기서 epoch 을 올리면
  // 막 시작한 진행이 — 효과가 flush 되기 전에 눌린 「올리기」가 — 조용히 버려진다.
  // 울타리는 epoch 없이도 범위를 본다: `aliveFor` 가 잡아 둔 scope 와 지금 `scopeRef` 를
  // 비교하고, `scopeRef` 는 렌더 중에 갱신되므로 효과 시점과 무관하게 맞다.
  useEffect(() => () => {
    transferRef.current?.abort();
    transferRef.current = null;
  }, [scopeKey]);

  const inFlight = [
    UPLOAD_PHASE.DIGESTING, UPLOAD_PHASE.RESERVING, UPLOAD_PHASE.TRANSFERRING,
    UPLOAD_PHASE.FINALIZING, UPLOAD_PHASE.ATTACHING,
  ].includes(upload.phase);
  const disabled = busy || inFlight;

  function chooseFile(picked) {
    setLocalError(null);
    if (!picked) return;
    const verdict = validateFileForUpload(picked, limits);
    if (!verdict.ok) {
      // 25MiB 를 다 보낸 뒤에 413 을 보는 일을 없앤다. **판정은 여전히 서버가 한다** —
      // 여기서 통과해도 서버가 매직바이트로 거절할 수 있다.
      setLocalError(verdict.message);
      setDraft((prior) => ({ ...prior, file: null, upload: advanceUpload(prior.upload, { type: 'clear' }) }));
      return;
    }
    setDraft((prior) => ({
      ...prior,
      file: picked,
      // 제목은 파일 이름에서 시작하되 **사람이 고칠 수 있다.** 이미 적어 둔 제목은 덮지 않는다.
      title: prior.title?.trim() ? prior.title : deriveTitle(picked.name),
      upload: advanceUpload(prior.upload, { type: 'select', file: picked }),
    }));
  }

  /**
   * 이 컴포넌트가 지금 이 자리에 살아 있나.
   *
   * 세 가지가 울타리를 무너뜨린다: unmount · 범위(Space·Node·대상 자산) 변경 · 새 시도 시작.
   * 무너진 뒤에 돌아온 응답은 **아무것도 쓰지 않는다** — 이미 없는 자리의 값이거나,
   * 더 나쁘게는 **다른 자리**의 초안을 덮어쓸 값이다.
   */
  const aliveFor = (epoch, scope) => () => (
    mountedRef.current && epochRef.current === epoch && scopeRef.current === scope
  );

  /** 주입할 네트워크. 컴포넌트가 아는 것(세션·Space)을 여기서 묶는다. */
  const networkDeps = () => ({
    digest: (picked) => digestFileSha256(picked, { maxBytes: limits?.maxFileBytes ?? 0 }),
    reserve: (payload, { idempotencyKey }) => initResourceUpload(
      payload, { nodeId: session.nodeId, idempotencyKey },
    ),
    transfer: ({ uploadId, contentPath, file: picked, onProgress, signal }) => transferResourceUpload({
      // 서버가 알려 준 경로는 **모양을 확인한 뒤에만** 쓴다. 어긋나면 우리가 아는 경로다.
      path: uploadStepPath('content', uploadId, contentPath),
      spaceId,
      nodeId: session.nodeId,
      file: picked,
      onProgress,
      signal,
    }),
    finalize: ({ uploadId, finalizePath, idempotencyKey }) => finalizeResourceUpload({
      path: uploadStepPath('finalize', uploadId, finalizePath),
      spaceId,
      nodeId: session.nodeId,
      idempotencyKey,
    }),
    abort: ({ uploadId, idempotencyKey }) => abortResourceUpload({
      path: uploadStepPath('abort', uploadId, null),
      spaceId,
      nodeId: session.nodeId,
      idempotencyKey,
    }),
    attach: attach ? (manifest) => attach(manifest) : undefined,
    newKey: newActionKey,
    describe: (error) => describeError(error).message,
  });

  /** 실패한 걸음부터 이어서 한다. `step` 을 주지 않으면 지금 상태가 정한다. */
  async function run(step = null) {
    if (runningRef.current || disabled) return;

    const at = step ?? resumeStep(upload);
    if (at === null) return;

    // **검사는 걸음에 맞춰서만 한다.** 이미 저장이 끝난 파일을 붙이는 일에 보관 공간을
    // 다시 따지면, 그사이 공간이 찼다는 이유로 붙이기가 영원히 막힌다 —
    // 그런데 그 파일은 이미 그 공간을 쓰고 있다.
    const needsFile = at === UPLOAD_STEP.RESERVE || at === UPLOAD_STEP.TRANSFER;
    if (needsFile && !file) {
      setLocalError('올릴 파일을 고르세요.');
      return;
    }
    // 예약은 **그 파일**을 위해 잡은 자리다. 다른 파일을 그 자리로 보내면 서버가 크기·해시
    // 에서 거절하고, 사람은 왜 거절됐는지 모른다. 보내기 전에 여기서 말한다.
    if (at === UPLOAD_STEP.TRANSFER && upload.reservedIntent && file) {
      const swapped = intentDiffers(upload, {
        ...upload.reservedIntent,
        filename: file.name,
        byteSize: Number(file.size ?? 0),
      });
      if (swapped) {
        setLocalError('처음 고른 파일과 다릅니다. 「취소하고 처음부터」를 누른 뒤 다시 올려 주세요.');
        return;
      }
    }

    let intent = null;
    if (at === UPLOAD_STEP.RESERVE) {
      const verdict = validateFileForUpload(file, limits);
      if (!verdict.ok) {
        setLocalError(verdict.message);
        return;
      }
      const title = draft.title?.trim();
      if (!title) {
        setLocalError('자료 이름을 적으세요.');
        return;
      }
      intent = {
        spaceId,
        ...(assetId ? { assetId } : {}),
        kind: verdict.kind,
        title,
        filename: file.name,
        mediaType: verdict.mediaType,
        byteSize: verdict.byteSize,
      };
    }
    setLocalError(null);
    runningRef.current = true;

    // 새 시도는 앞선 시도의 울타리를 무너뜨린다. 두 진행이 같은 초안에 쓰지 않게.
    epochRef.current += 1;
    const epoch = epochRef.current;
    const scope = scopeRef.current;
    const controller = new AbortController();
    transferRef.current = controller;

    // **처음부터 다시**라면 들고 있던 자리와 키를 먼저 놓는다. 그러지 않으면 만료된
    // 예약의 키로 다시 예약하게 되고, 서버는 그 닫힌 예약을 그대로 돌려준다 — 그 위에서
    // 전송은 영원히 실패한다.
    // **키를 놓는 것은 예약이 죽은 것이 확인됐을 때뿐이다.**
    //
    // 예약에는 두 가지 「처음부터」가 있고, 둘을 섞으면 멱등성이 깨진다:
    //
    //   · 죽은 자리 — 서버가 `upload_expired` · `upload_not_found` 라고 **말해 줬다.**
    //     그 키로 다시 부르면 영원히 같은 대답이므로, 자리와 키를 함께 놓고 새로 시작한다.
    //   · 모르는 자리 — 예약 요청은 나갔는데 **응답을 못 받았다**(연결 끊김·시간 초과).
    //     서버에 예약이 생겼는지 우리는 알 수 없다. 여기서 키를 버리고 새로 예약하면
    //     같은 파일의 자리를 둘 잡게 되고, 그 하나는 아무도 쓰지 않은 채 할당량만 먹는다.
    //     그래서 **같은 키·같은 본문으로 다시 부른다** — 서버가 첫 예약을 그대로 돌려준다.
    //
    // (사람이 「취소하고 처음부터」를 누른 경우는 `cancel()` 이 이미 자리를 닫고 키를 비운다.)
    const reservationConfirmedDead = at === UPLOAD_STEP.RESERVE
      && upload.uploadId !== null
      && needsFreshReservation(upload.error?.code ?? null);

    let startState = upload;
    if (reservationConfirmedDead) {
      const stale = upload;
      setUpload({ type: 'cancelled' });
      startState = advanceUpload(upload, { type: 'cancelled' });
      // 놓아 주기는 기다리지 않는다 — 이미 만료된 자리면 서버도 할 일이 없다.
      releaseReservation(stale, networkDeps()).then((outcome) => {
        if (outcome.released) invalidate(`space:${spaceId}:upload-limits`);
      });
    }

    try {
      const result = await runUpload({
        from: at,
        state: startState,
        intent,
        file,
        deps: networkDeps(),
        emit: (event) => setUpload(event),
        alive: aliveFor(epoch, scope),
        signal: controller.signal,
      });

      if (!aliveFor(epoch, scope)()) return;
      if (result.outcome === RUN_OUTCOME.DONE) {
        // **판이 생겼다.** 목록과 남은 공간이 바뀌었으므로 서버 기준으로 다시 읽는다(D-9).
        invalidate(`space:${spaceId}:resources`);
        invalidate(`space:${spaceId}:upload-limits`);
        onUploaded?.(result.manifest ?? result.state.manifest);
      } else if (result.state.manifest && result.outcome === RUN_OUTCOME.FAILED) {
        // 저장까지는 끝났다 — 목록에는 이미 그 판이 있다.
        invalidate(`space:${spaceId}:resources`);
        invalidate(`space:${spaceId}:upload-limits`);
      }
    } finally {
      if (transferRef.current === controller) transferRef.current = null;
      runningRef.current = false;
    }
  }

  /**
   * 사람이 멈춘다. 예약한 자리를 **놓아 주고** 진행을 되돌린다.
   * 확정이 끝난 업로드는 놓지 않는다 — 그건 이미 판이다.
   */
  async function cancel() {
    epochRef.current += 1;
    transferRef.current?.abort();
    transferRef.current = null;
    const snapshot = upload;
    setUpload({ type: 'cancelled' });
    const outcome = await releaseReservation(snapshot, networkDeps());
    if (outcome.released) invalidate(`space:${spaceId}:upload-limits`);
  }

  /**
   * 고른 파일을 바꾸거나 지운다.
   *
   * **앞의 예약을 먼저 놓아 준다.** 그러지 않으면 서버에는 아무도 쓰지 않을 자리가 남고,
   * 그 크기만큼 이 Space 의 보관 공간이 TTL 동안 잠긴다. 확정된 판은 놓지 않는다.
   */
  async function replaceSelection(picked) {
    const snapshot = upload;
    epochRef.current += 1;
    transferRef.current?.abort();
    transferRef.current = null;

    if (picked) chooseFile(picked);
    else setDraft((prior) => ({ ...prior, file: null, upload: advanceUpload(prior.upload, { type: 'clear' }) }));

    const outcome = await releaseReservation(snapshot, networkDeps());
    if (outcome.released) invalidate(`space:${spaceId}:upload-limits`);
  }

  const percent = progressPercent(upload.loaded, file?.size ?? upload.file?.size);
  // 저장이 끝난 자리 — 판은 이미 존재한다. 여기서는 파일을 지우지도, 다시 올리지도 않는다.
  const saved = Boolean(upload.manifest);
  // 예약이 시작됐다면 그 본문은 굳었다(같은 키에 다른 본문 = 영원한 거절).
  const nameLocked = Boolean(upload.reserveKey) && !saved;
  const errorText = upload.error
    ? uploadErrorMessage(upload.error.code, { limits, fallback: upload.error.message })
    : null;
  const retryable = upload.error ? canRetryUpload(upload.error.code, upload.error.step) : true;
  // 다시 열었을 때(대화상자를 닫아 전송이 끊겼을 때)도 **어디서부터인지**가 버튼에 보인다.
  const resumeAt = resumeStep(upload);
  const retryStep = upload.error?.step
    ?? (resumeAt && resumeAt !== UPLOAD_STEP.RESERVE ? resumeAt : null);
  const finished = upload.phase === UPLOAD_PHASE.DONE;
  // 다음 걸음이 파일을 필요로 하나 — 붙이기만 남았다면 파일 없이도 누를 수 있어야 한다.
  const nextStep = retryStep ?? resumeStep(upload);
  const needsFileNow = nextStep === UPLOAD_STEP.RESERVE || nextStep === UPLOAD_STEP.TRANSFER;

  return (
    <div class="uploader">
      {/* 드롭 영역이자 파일 고르기. 끌어다 놓는 길만 두지 않는다 — 키보드로도 골라야 한다. */}
      <div
        class={`uploader__drop${dragging ? ' uploader__drop--over' : ''}`}
        onDragOver={(event) => { event.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={(event) => {
          event.preventDefault();
          setDragging(false);
          if (disabled) return;
          replaceSelection(event.dataTransfer?.files?.[0] ?? null);
        }}
      >
        <span class="uploader__glyph"><Icon name="resource" /></span>
        <div class="uploader__drop-text">
          <p class="t-body-strong">파일을 여기에 끌어다 놓거나 골라 주세요.</p>
          <p class="t-meta">
            {allowedExtensionsLabel(limits)} · 최대 {fileSizeLabel(limits?.maxFileBytes) ?? '—'}
            {Number.isFinite(limits?.spaceRemainingBytes)
              ? ` · 이 Space에 ${fileSizeLabel(limits.spaceRemainingBytes)} 남음`
              : ''}
          </p>
        </div>
        {/* 입력이 라벨보다 **앞**이다: 보이지 않는 입력에 초점이 갔을 때 뒤따르는 라벨에
            테두리를 그려 주려면 형제 선택자가 필요하다. 키보드로도 고를 수 있어야 한다. */}
        <input
          id={`${idPrefix}-file`}
          class="visually-hidden uploader__input"
          type="file"
          accept={acceptAttribute(limits)}
          disabled={disabled}
          onChange={(event) => {
            replaceSelection(event.currentTarget.files?.[0] ?? null);
            // 같은 파일을 다시 고를 수 있게 값을 비운다(그러지 않으면 change 가 안 난다).
            event.currentTarget.value = '';
          }}
        />
        <label class="btn btn--neutral btn--sm uploader__pick" for={`${idPrefix}-file`}>
          <span class="btn__label">파일 고르기</span>
        </label>
      </div>

      {file ? (
        <div class="uploader__file">
          <div class="grow">
            <p class="t-body uploader__filename">{file.name}</p>
            <p class="t-meta">{fileSizeLabel(file.size) ?? '크기를 읽지 못함'} · {file.type || '형식 미상'}</p>
          </div>
          {/* 이미 저장이 끝난 파일은 여기서 지우지 않는다 — 선택을 지운다고 올라간 파일이
              사라지지는 않고, 지우면 「붙이기만 다시 시도」로 돌아갈 길이 없어진다. */}
          {saved ? null : upload.phase === UPLOAD_PHASE.IDLE || upload.phase === UPLOAD_PHASE.ERROR ? (
            <Button size="sm" tone="ghost" onClick={() => replaceSelection(null)}>고른 파일 지우기</Button>
          ) : null}
        </div>
      ) : null}

      <Field
        label="자료 이름"
        htmlFor={`${idPrefix}-title`}
        required
        hint={nameLocked
          ? '올리기를 시작한 뒤에는 이름을 바꿀 수 없습니다. 바꾸려면 취소하고 다시 올려 주세요.'
          : '파일 이름에서 가져왔습니다. 고쳐도 됩니다.'}
      >
        <Input
          id={`${idPrefix}-title`}
          size="sm"
          value={draft.title ?? ''}
          /* 예약이 시작된 뒤에 이름을 바꾸면, 같은 멱등 키에 다른 본문이 나가 서버가
             영원히 거절한다. 그래서 그 시점부터 이름은 잠긴다. */
          disabled={disabled || nameLocked}
          onInput={(event) => setDraft((prior) => ({ ...prior, title: event.currentTarget.value }))}
        />
      </Field>

      {/* 상태는 조용히 말한다(polite). 재촉하지 않는다(§절대규칙 2). */}
      <p class="t-meta uploader__status" role="status" aria-live="polite">
        {finished
          ? `올렸습니다 — ${upload.manifest?.title ?? ''} 등록 판 v${upload.manifest?.registeredVersion ?? '?'}`
          : uploadPhaseLabel(upload)}
      </p>

      {upload.phase === UPLOAD_PHASE.TRANSFERRING && percent !== null ? (
        <div class="uploader__progress">
          <progress class="uploader__bar" value={upload.loaded} max={file?.size ?? upload.file?.size ?? 0} />
          <Button size="sm" tone="ghost" onClick={cancel}>전송 취소</Button>
        </div>
      ) : null}

      {localError ? <Notice tone="danger" title={localError} /> : null}
      {upload.error ? (
        <Notice tone="danger" title={errorText}>
          {retryStep === UPLOAD_STEP.ATTACH ? (
            <p class="t-meta">파일은 이미 저장되었습니다. 다시 올리지 않고 붙이기만 다시 합니다.</p>
          ) : retryStep === UPLOAD_STEP.TRANSFER ? (
            <p class="t-meta">올릴 자리는 그대로 있습니다. 보내는 것만 다시 합니다.</p>
          ) : retryable ? null : (
            <p class="t-meta">다시 눌러도 같은 결과가 나옵니다. 파일이나 권한을 확인해 주세요.</p>
          )}
        </Notice>
      ) : null}

      <div class="row">
        {finished ? null : (
          <>
            <Button
              size="md"
              tone="human"
              busy={inFlight}
              disabled={disabled || (needsFileNow && !file) || !retryable}
              onClick={() => run(retryStep)}
            >
              {retryStep ? retryLabel(retryStep) : '올리기'}
            </Button>
            {/* 예약을 잡아 둔 채 멈춘 자리에서 나갈 길. 이걸 없애면 이름을 고치려는
                사람이 갇힌다(이름은 예약이 시작되면 잠기므로). */}
            {upload.uploadId && !saved && upload.phase !== UPLOAD_PHASE.TRANSFERRING ? (
              <Button size="sm" tone="ghost" onClick={cancel}>취소하고 처음부터</Button>
            ) : null}
          </>
        )}
      </div>
    </div>
  );
}
